import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import storage from "redux-persist/lib/storage";
import { persistReducer, persistStore } from "redux-persist";

import { authApi } from "./apis/authApi";
import { dashboardPageApi } from "./apis/dashboardPageApi";
import { offerDetailPageApi } from "./apis/offerDetailPageApi";
import { applicantDetailPageApi } from "./apis/applicantDetailPageApi";
import { insuranceDetailsApi } from "./apis/insuranceDetailsApi";
import { nomineeDetailsApi } from "./apis/nomineeDetailsApi";
import { mandateApi } from "./apis/mandateApi";
import { eSignApi } from "./apis/eSignApi";
import { congratulationsApi } from "./apis/congratulationsApi";
import { verifyPanCardApi } from "./apis/panVerificationApi";
import { digiKycInitDataApi } from "./apis/digiKycInitApi";
import { digiKycRespApi } from "./apis/digiKycResponse";
import { clientLogsApi } from "./apis/clientLogsApi";
import { userDeviceInfoApi } from "./apis/userDeviceInfoApi";
import { createCallerCodeApi } from "./apis/createCallerCodeApi";
import { referenceDetailsApi } from "./apis/referenceDetailsPageApi";
import { alternateAddressApi } from "./apis/alternateAddressApi";
import { checkOfferEligibilityApi } from "./apis/checkOfferEligibilityApi";
import { vkycApi } from "./apis/vkycApi.js";

import serviceProviderReducer from "./slices/serviceProviderSlice";

// Combine all reducers
const rootReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [dashboardPageApi.reducerPath]: dashboardPageApi.reducer,
  [offerDetailPageApi.reducerPath]: offerDetailPageApi.reducer,
  [applicantDetailPageApi.reducerPath]: applicantDetailPageApi.reducer,
  [insuranceDetailsApi.reducerPath]: insuranceDetailsApi.reducer,
  [nomineeDetailsApi.reducerPath]: nomineeDetailsApi.reducer,
  [mandateApi.reducerPath]: mandateApi.reducer,
  [eSignApi.reducerPath]: eSignApi.reducer,
  [congratulationsApi.reducerPath]: congratulationsApi.reducer,
  [verifyPanCardApi.reducerPath]: verifyPanCardApi.reducer,
  [digiKycInitDataApi.reducerPath]: digiKycInitDataApi.reducer,
  [digiKycRespApi.reducerPath]: digiKycRespApi.reducer,
  [clientLogsApi.reducerPath]: clientLogsApi.reducer,
  [userDeviceInfoApi.reducerPath]: userDeviceInfoApi.reducer,
  [createCallerCodeApi.reducerPath]: createCallerCodeApi.reducer,
  [referenceDetailsApi.reducerPath]: referenceDetailsApi.reducer,
  [alternateAddressApi.reducerPath]: alternateAddressApi.reducer,
  [checkOfferEligibilityApi.reducerPath]: checkOfferEligibilityApi.reducer,
  [vkycApi.reducerPath]: vkycApi.reducer,

  // Slice reducer
  serviceProvider: serviceProviderReducer,
});

// Configure persistence
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["serviceProvider"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(authApi.middleware)
      .concat(dashboardPageApi.middleware)
      .concat(offerDetailPageApi.middleware)
      .concat(applicantDetailPageApi.middleware)
      .concat(insuranceDetailsApi.middleware)
      .concat(nomineeDetailsApi.middleware)
      .concat(mandateApi.middleware)
      .concat(eSignApi.middleware)
      .concat(congratulationsApi.middleware)
      .concat(verifyPanCardApi.middleware)
      .concat(digiKycInitDataApi.middleware)
      .concat(digiKycRespApi.middleware)
      .concat(clientLogsApi.middleware)
      .concat(userDeviceInfoApi.middleware)
      .concat(createCallerCodeApi.middleware)
      .concat(referenceDetailsApi.middleware)
      .concat(alternateAddressApi.middleware)
      .concat(checkOfferEligibilityApi.middleware)
      .concat(vkycApi.middleware)
});

setupListeners(store.dispatch);

// Export persistor
export const persistor = persistStore(store);

export {
  useGenerateOtpMutation,
  useVerifyotpMutation,
  useLazyGetContactHintQuery,
} from "./apis/authApi";

export {
  useStageStatusMutation,
  useGetAllOffersQuery,
  useGetLoanInfoQuery,
  useCreateNewLoanMutation,
  useGetEmiDetailsMutation,
  useGetForeclosureDataMutation
} from "./apis/offerDetailPageApi";

export {
  useOldKycDocumentMutation,
  useBankDetailPennyDropMutation,
  useGetHvTokenQuery,
  useFaceMatchMutation,
} from "./apis/applicantDetailPageApi";

export {
  useCreateInsuranceDetailsMutation,
  useUpdateLoanMutation,
} from "./apis/insuranceDetailsApi";

export { useNomineeDetailsMutation } from "./apis/nomineeDetailsApi";

export {
  useCreateMandateMutation,
  useMandateStatusMutation,
  useMandateAvailabilityMutation,
  useVerifiyUpiIdMutation,
  useCheckUpiMandateStatusMutation,
  useCheckOpenMandateStatusMutation
} from "./apis/mandateApi";

export {
  useFetchESignDocMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useESignDocQuery,
  useESignDisburseMutation,
  useESignKFSMutation,
} from "./apis/eSignApi";

export {
  useFetchSanctionLetterDocMutation,
  useUserFeedbackMutation,
} from "./apis/congratulationsApi";

export {
  useClientLogsMutation
} from "./apis/clientLogsApi";

export {
  useUserDeviceInfoMutation
} from "./apis/userDeviceInfoApi";

export {
  useCreateCallerCodeMutation
} from "./apis/createCallerCodeApi";

export { useDigiKycInitMutation } from "./apis/digiKycInitApi";

export { useDigiKycRespMutation } from "./apis/digiKycResponse";
export { useVerifyPanMutation } from "./apis/panVerificationApi";

export { useGetDashboardQuery } from "./apis/dashboardPageApi";

export { useReferenceDetailsMutation } from "./apis/referenceDetailsPageApi";

export { useAddAlternateAddressMutation, usePincodeDetailsQuery } from "./apis/alternateAddressApi";

export { useLazyCheckOfferEligibilityQuery } from "./apis/checkOfferEligibilityApi";

export { usePreVkycDetailsMutation, useProcessVkycResponseMutation } from "./apis/vkycApi";
